import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportDateService } from 'src/app/services/report-date.service';
import { UtilityService } from 'src/app/services/utility.service';
import { CurrencyService } from '../../../services/currency.service';

export interface ReportDetailsDialogData {
  year: number;
  week: string;
  reportType: string;
  reportData: any;
}

@Component({
  selector: 'app-report-details-dialog',
  templateUrl: './report-details-dialog.component.html',
  styleUrls: ['./report-details-dialog.component.css']
})
export class ReportDetailsDialogComponent implements OnInit {
  Math = Math;
  reportTitle: string = '';
  reportIcon: string = '';
  staffPerformanceColumns: string[] = [
    'name', 'number_guests', 'private_lessons', 'number_in_class',
    'dor_sanct_competition', 'showcase_medal_ball', 'party_time_non_unit',
    'interview_department', 'renewal_department'
  ];

  constructor(
    public dialogRef: MatDialogRef<ReportDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportDetailsDialogData,
    private utilityService: UtilityService,
    private reportDateService: ReportDateService,
    private currencyService: CurrencyService,
  ) {}

  ngOnInit(): void {
    this.setReportMetadata();
  }

  setReportMetadata(): void {
    switch (this.data.reportType) {
      case 'royalty':
        this.reportTitle = 'Royalty Report';
        this.reportIcon = 'account_balance';
        break;
      case 'staff_performance':
        this.reportTitle = 'Staff Performance Report';
        this.reportIcon = 'assessment';
        break;
      case 'studio_business':
        this.reportTitle = 'Studio Business Report';
        this.reportIcon = 'business_center';
        break;
      case 'miscellaneous':
        this.reportTitle = 'Miscellaneous Report';
        this.reportIcon = 'more_horiz';
        break;
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return this.reportDateService.formatDate(dateString, 'N/A');
  }

  formatCurrency(amount: any): string {
    // Use the studio's currency symbol (€/£/$) rather than a hardcoded USD symbol.
    return this.currencyService.format(amount);
  }

  formatPercentage(value: any): string {
    if (value === null || value === undefined) return '0%';
    return `${value}%`;
  }

  getLineItemKeys(): string[] {
    if (!this.data.reportData?.line_items?.length) return [];
    return Object.keys(this.data.reportData.line_items[0]);
  }

  getStudioName(): string {
    return this.data.reportData?.studio?.name || 'N/A';
  }

  getStudioAddress(): string {
    const studio = this.data.reportData?.studio;
    if (!studio) return 'N/A';
    return `${studio.address_line_1}${studio.address_line_2 ? ' ' + studio.address_line_2 : ''}, ${studio.city}, ${studio.state} ${studio.zip}`;
  }

  getIconColorClass(): string {
    return this.data.reportType;
  }

  getStaffByType(type: string): any[] {
    if (!this.data.reportData?.line_items?.length) return [];
    return this.data.reportData.line_items.filter((item: any) => item.staff_type === type);
  }

  getStaffMemberName(item: any): string {
    if (!item.staff_member) return 'N/A';
    const first = item.staff_member.first_name || '';
    const last = item.staff_member.last_name || '';
    return `${first} ${last}`.trim() || 'N/A';
  }

  getRoyaltyExecutiveName(item: any): string {
    if (!item.executive) return '';
    return `${item.executive.first_name || ''} ${item.executive.last_name || ''}`.trim();
  }

  getRoyaltyStaffName(item: any): string {
    if (!item.staff_members?.length) return '';
    return item.staff_members.map((s: any) => `${s.first_name || ''} ${s.last_name || ''}`.trim()).join(', ');
  }

  getRoyaltyLineTotal(item: any): number {
    return (item.cash || 0) + (item.miscellaneous_services || 0) + (item.sundry || 0);
  }

  getRoyaltySummary(): any {
    if (this._royaltySummaryCache) return this._royaltySummaryCache;
    const items = this.data.reportData?.line_items || [];
    const positives = items.filter((i: any) => this.getRoyaltyLineTotal(i) >= 0);
    const negatives = items.filter((i: any) => this.getRoyaltyLineTotal(i) < 0);

    const sum = (arr: any[], field: string) => arr.reduce((acc: number, i: any) => acc + (i[field] || 0), 0);

    const cash = sum(positives, 'cash');
    const misc = sum(positives, 'miscellaneous_services');
    const sundry = sum(positives, 'sundry');
    const refCash = sum(negatives, 'cash');
    const refMisc = sum(negatives, 'miscellaneous_services');
    const refSundry = sum(negatives, 'sundry');

    this._royaltySummaryCache = {
      cash, miscellaneous_services: misc, sundry, total: cash + misc + sundry,
      refund_cash: refCash, refund_miscellaneous: refMisc, refund_sundry: refSundry,
      refund_total: refCash + refMisc + refSundry,
      net_cash: cash + refCash, net_miscellaneous: misc + refMisc, net_sundry: sundry + refSundry,
      net_total: cash + misc + sundry + refCash + refMisc + refSundry
    };
    return this._royaltySummaryCache;
  }
  private _royaltySummaryCache: any = null;

  getMiscTeacherName(item: any): string {
    if (!item.teachers?.length) return '';
    return item.teachers.map((t: any) => `${t.first_name || ''} ${t.last_name || ''}`.trim()).join(', ');
  }

  getMiscTotals(): any {
    if (this._miscTotalsCache) return this._miscTotalsCache;
    const items = this.data.reportData?.line_items || [];
    this._miscTotalsCache = {
      totalChargesDue: items.reduce((acc: number, i: any) => acc + (i.total_charges_due || 0), 0),
      totalPayments: items.reduce((acc: number, i: any) => acc + (i.payment_amount || 0), 0)
    };
    return this._miscTotalsCache;
  }
  private _miscTotalsCache: any = null;

  formatCurrencyShort(amount: any): string {
    if (amount === null || amount === undefined) return `${this.currencyService.getSymbol()}0`;
    const num = Number(amount);
    if (num >= 1000) {
      return `${this.currencyService.getSymbol()}${(num / 1000).toFixed(1)}k`;
    }
    return this.utilityService.formatCurrency(num, {
      includeSymbol: true,
      decimals: 0,
      useGrouping: false
    });
  }
}
